// MapSelector.tsx
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import * as L from 'leaflet';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Box, Button, FormLabel, SimpleGrid, Input } from '@chakra-ui/react';
import { SetMapRef } from '../../Pages/SparksMap';
import { FaMapMarkedAlt } from 'react-icons/fa';
import { FiMapPin } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { AddOrgFormValues } from '../../utils/helper';
import { FormikProps } from 'formik';

// Fix Leaflet marker icons for Vite
delete (L.Icon.Default.prototype as any)._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/leaflet/marker-icon-2x.png',
  iconUrl: '/images/leaflet/marker-icon.png',
  shadowUrl: '/images/leaflet/marker-shadow.png',
});

interface MapSelectorProps {
  formik: FormikProps<AddOrgFormValues>,
  closeModal: () => void;
}

const MapSelector = ({ formik, closeModal }: MapSelectorProps) => {
  const [position, setPosition] = useState<[number, number] | null>([39.8283, -98.5795]);
  const [longitude, setLongitude] = useState("");
  const [latitude, setLatitude] = useState("");
  const mapRef = useRef<L.Map | null>();
  // Try to get user's current location

  // default position: [37.92686760148135,-98.30566406250001]

  useEffect(() => {
    if(longitude && latitude){

      const newPosition = [parseFloat(latitude), parseFloat(longitude)] as [number, number];

       if(mapRef.current){
          mapRef.current.flyTo(newPosition,10, { animate: true, duration: 1 });
        }
      

      setPosition(newPosition);
    }
  },[longitude, latitude])

  useEffect(() => {

    if(position?.[0] && position?.[1]){

      setLatitude(position[0].toString());
      setLongitude(position[1].toString())
    }

  },[position?.[0], position?.[1]])


  const geoLocateMe = () => {
       navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        const newPosition: [number, number] = [latitude, longitude];
        setPosition(newPosition);

        if(mapRef.current){
          mapRef.current.flyTo(position,10, { animate: true, duration: 1 });
        }
        toast.success("We geolocated your position");
      },
      (err) => {
        console.warn('Geolocation error:', err);
        toast.error("We couldn't geolocate your position. Make sure you have given the authorization do so")
        setPosition([51.505, -0.09]);
      }
    );
  }

     const zoomToUS = () => {
        if (mapRef.current) {
            mapRef.current.flyTo([39.8283, -98.5795], 4, { animate: true, duration: 1 });
        }
     };

  // Custom hook to handle map clicks
  const LocationMarker = () => {
    useMapEvents({
      click(e) {
        const newPosition: [number, number] = [
          e.latlng.lat,
          e.latlng.lng,
        ];
        setPosition(newPosition);
      },
    });

    return position ? <Marker position={position} /> : null;
  };

  console.log("POSITION: ", position);
    const setMapRef = (map: L.Map) => {
        mapRef.current = map;
    };


  return (
    <div style={{ height: '100%', width: '100%', backgroundColor: 'white' }}>
      <p style={{ marginBottom: 8 }}>
  To manually locate your position on the map, use the <span style={{ fontWeight: 'bold', fontSize: 24 }}>+</span> and <span style={{ fontWeight: 'bold', fontSize: 24 }}>−</span> buttons to zoom in or out, and click-and-drag to navigate across the map. Once you identify the street where your organization is located, click on the desired location to select it. Then click the "Submit position" button to submit your location.
</p>
<p style={{ marginBottom: 8 }}>
  Alternatively, you can use an external map service such as <Link target="_blank" to="https://map.google.com">Google Maps</Link> to find your business location. After locating it, copy the latitude and longitude coordinates and paste them in the fields provided below.
</p>


      <SimpleGrid columns={{base: 1, md: 2, sm: 1}} gap={8} mt={8}>
        <Box>
            <FormLabel>
              Longitude
            </FormLabel>
            <Input
                value={longitude}
                onChange={(e) =>{ 
                  setLongitude(e.target.value)
                }}
                className="flushed"
                {...{ maxLength: 50 }}
                type="number"
            />
        </Box>
        <Box>
          <FormLabel>
            Latitude
          </FormLabel>
          <Input
                value={latitude}
                onChange={(e) =>{
                  setLatitude(e.target.value)
                }}
                type="number"
                className="flushed"
                {...{ maxLength: 50 }}

            />
        </Box>
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, md: 3, lg: 3, sm: 1}} gap={8} mt={8} mb={8}>
        <Box>
          <Button onClick={geoLocateMe}>
            <FiMapPin style={{marginRight: 8}} size={24}/>
            Geolocate me
          </Button>
        </Box>
        <Box>
          <Button onClick={zoomToUS}>
            <FaMapMarkedAlt  style={{marginRight: 8}}  size={24} />
            Show the entire U.S. map
          </Button>
        </Box>
        <Box>
          <Button onClick={() => {
            formik.setFieldValue("location.latitude", position[0]);
            formik.setFieldValue("location.longitude", position[1]);
            closeModal();
          }}>
            Submit my position
          </Button>
        </Box>
      </SimpleGrid>
      {position && (
        <MapContainer
          center={position}
          zoom={13}
          scrollWheelZoom={true}
          style={{ height: '500px', width: '100%' }}
        >
          <SetMapRef setMapRef={setMapRef} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <LocationMarker />
        </MapContainer>
      )}
     
    </div>
  );
};

export default MapSelector;
